/**
 * AuditCard — last N admin audit entries scoped to a single match.
 *
 * Reads `recent_admin_actions` from the parent's GET /matches/:id response
 * (which is itself the last 20 entries from the shared audit log).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2
 */
export interface AuditEntry {
  id?: string;
  admin_user_id?: string | null;
  admin_email?: string | null;
  admin_name?: string | null;
  action?: string;
  entity_type?: string;
  entity_id?: string | null;
  details?: Record<string, unknown> | null;
  created_at?: string;
}

interface Props {
  entries: AuditEntry[];
}

export function AuditCard({ entries }: Props) {
  return (
    <section className="mt-4 rounded-md border bg-card p-3 text-card-foreground">
      <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>Recent admin actions</h3>
      {(!entries || entries.length === 0) && (
        <p className="text-[13px] italic text-muted-foreground">
          No actions recorded for this match yet.
        </p>
      )}
      {entries && entries.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {entries.map((e, i) => (
            <li
              key={e.id ?? `${e.created_at}-${i}`}
              className={i === 0 ? '' : 'border-t'}
              style={{
                padding: '6px 0',
                fontSize: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{e.action ?? '—'}</span>
                <span className="text-muted-foreground">
                  {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                </span>
              </div>
              <div className="text-muted-foreground">
                {e.admin_name || e.admin_email || e.admin_user_id || 'unknown'}
              </div>
              {e.details && Object.keys(e.details).length > 0 && (
                <details style={{ marginTop: 2 }}>
                  <summary className="cursor-pointer text-muted-foreground">
                    details
                  </summary>
                  <pre
                    className="mt-1 overflow-auto rounded bg-muted p-1.5 text-[11px]"
                  >
                    {JSON.stringify(e.details, null, 2)}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
